/**
 * 邮百工GUI渲染逻辑统一 - 核心Markdown渲染组件
 * 统一处理Markdown、Mermaid图表和表格的渲染
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CoreMermaid from './CoreMermaid';
import CoreTable from './CoreTable';
import { processContent, detectContentType } from './ContentUtils';

/**
 * 核心Markdown渲染组件
 * @param {Object} props - 组件属性
 * @param {string} props.content - Markdown内容
 * @param {string} props.className - 额外的CSS类名
 * @returns {JSX.Element} - 渲染后的Markdown组件
 */
const CoreMarkdown = ({ content, className = '' }) => {
  // 预处理内容 - 确保钩子在每次渲染时都以相同顺序调用
  const processedData = useMemo(() => 
    content ? processContent(content) : { type: 'markdown', content: '' }, 
  [content]);
  
  // 如果内容为空，返回空元素
  if (!content) return null;
  
  // 如果内容是单独的Mermaid图表，直接使用CoreMermaid渲染
  if (processedData.type === 'mermaid') {
    return <CoreMermaid chart={processedData.content} className={className} />;
  }
  
  // 如果内容是单独的表格，直接使用CoreTable渲染
  if (processedData.type === 'markdown-table') {
    return <CoreTable content={processedData.content} className={className} />;
  }
  
  // 渲染Markdown内容
  return (
    <div className={`core-markdown ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // 代码块处理
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const codeContent = String(children).replace(/\n$/, "");
            
            // 处理Mermaid代码块
            if (!inline && match && match[1] === 'mermaid') {
              return <CoreMermaid chart={codeContent} />;
            }
            
            // 普通代码块
            if (!inline && match) {
              return (
                <div className="code-block-wrapper">
                  <div className="code-block-header">
                    <span className="code-language">{match[1]}</span>
                  </div>
                  <pre className={className} {...props}>
                    <code className={`language-${match[1]}`} {...props}>
                      {codeContent}
                    </code>
                  </pre>
                </div>
              );
            }
            
            // 行内代码
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          
          // 表格处理
          table({ node, children, ...props }) {
            // 提取表格内容
            const tableContent = node.children
              .map(n => {
                if (n.type === 'tableHead' || n.type === 'tableBody') {
                  return n.children.map(row => 
                    '|' + row.children
                      .map(cell => 
                        ' ' + (cell.children.map(c => c.value || '').join(' ') || '') + ' '
                      )
                      .join('|') + '|'
                  ).join('\n');
                }
                return '';
              })
              .filter(Boolean)
              .join('\n');
            
            // 处理表头和分隔行
            const tableRows = tableContent.split('\n');
            if (tableRows.length >= 1) {
              const headers = tableRows[0];
              const separatorCells = headers.split('|')
                .filter(Boolean)
                .map(() => '---');
              
              const separator = '|' + separatorCells.join('|') + '|';
              
              // 如果没有分隔行，添加一个
              if (tableRows.length === 1 || !tableRows[1].includes('-')) {
                tableRows.splice(1, 0, separator);
              }
            }
            
            const fullTableContent = tableRows.join('\n');
            
            // 使用CoreTable渲染
            return <CoreTable content={fullTableContent} />;
          },
          
          // 链接处理：外部链接在新窗口打开
          a({ href, children, ...props }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },

          // 普通段落处理
          p({ node, children, ...props }) {
            // 检查段落内容是否为Mermaid图表
            const content = node.children
              .map(child => child.value || '')
              .join('');
            
            if (detectContentType(content) === 'mermaid') {
              return <CoreMermaid chart={content} />;
            }
            
            // 常规段落
            return <p {...props}>{children}</p>;
          }
        }}
      >
        {processedData.content}
      </ReactMarkdown>
    </div>
  );
};

export default CoreMarkdown; 